package ro.horatiu_udrea.resursedeblocat

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import androidx.compose.foundation.background
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.Font
import resursedeblocat.composeapp.generated.resources.Res
import resursedeblocat.composeapp.generated.resources.*

enum class ResourceState {
    FRESH, OBTAINED, NOT_OBTAINED, FINISHED, ABANDONED
}

data class ResourceDetails(
    val id: String = "",
    val sheetId: String = "",
    val nume: String = "",
    val tip: String = "",
    val autor: String = "",
    val link: String = "",
    val imagine: String? = null,
    val categorii_match: List<String> = emptyList(),
    val sugestii: String? = null,
    val despre_resursa: String = "",
    val despre_autor: String = "",
    val parcurs_personal: String = "",
    val recomandat_daca: String = "",
    val sourceSubcategory: String = "",
    val addedAt: Long = 0,
    val state: ResourceState = ResourceState.FRESH
)

// Theme Colors
val ThemePrimary = Color(0xFF1D4A27) // Verde deschis
val ThemeTextDark = Color(0xFF0A2E0A) // Verde închis
val ThemeBackground = Color(0xFFF9F9F9) // Alb
val GeneralText = Color(0xFF262626)

val dummyResources = listOf(
    ResourceDetails(
        nume = "Meditații",
        tip = "Carte",
        autor = "Marcus Aurelius",
        link = "https://en.wikipedia.org/wiki/Meditations",
        imagine = "https://firebasestorage.googleapis.com/v0/b/deblocat.firebasestorage.app/o/mindvalley.png?alt=media&token=14c08e33-1e6b-47da-ab45-b795ee34b44d",
        categorii_match = listOf("Stoicism", "Filozofie", "Dezvoltare Personală"),
        despre_resursa = "O serie de scrieri personale ale împăratului roman Marcus Aurelius, care oferă îndrumări despre cum să trăiești o viață virtuoasă.",
        despre_autor = "Marcus Aurelius a fost împărat roman și filozof stoic.",
        parcurs_personal = "Această carte m-a ajutat să rămân calm în situații dificile.",
        recomandat_daca = "Recomand această carte oricui caută liniște interioară și perspectivă asupra vieții.",
        addedAt = 1000
    ),
    ResourceDetails(
        nume = "Atomic Habits",
        tip = "Carte",
        autor = "James Clear",
        link = "https://jamesclear.com/atomic-habits",
        imagine = "https://firebasestorage.googleapis.com/v0/b/deblocat.firebasestorage.app/o/mindvalley.png?alt=media&token=14c08e33-1e6b-47da-ab45-b795ee34b44d",
        categorii_match = listOf("Productivitate", "Obiceiuri", "Psihologie"),
        despre_resursa = "O metodă ușoară și dovedită de a-ți construi obiceiuri bune și de a scăpa de cele rele.",
        despre_autor = "James Clear este un autor și vorbitor specializat în obiceiuri și procese de îmbunătățire.",
        parcurs_personal = "Am reușit să îmi organizez mai bine rutina zilnică folosind sfaturile din această carte.",
        recomandat_daca = "Recomand această carte dacă vrei să îți schimbi comportamentul pe termen lung prin pași mici.",
        addedAt = 2000
    ),
    ResourceDetails(
        nume = "Huberman Lab",
        tip = "Podcast",
        autor = "Andrew Huberman",
        link = "https://hubermanlab.com/",
        imagine = "https://firebasestorage.googleapis.com/v0/b/deblocat.firebasestorage.app/o/mindvalley.png?alt=media&token=14c08e33-1e6b-47da-ab45-b795ee34b44d",
        categorii_match = listOf("Sănătate", "Știință", "Biohacking"),
        despre_resursa = "Discuții despre neuroștiință și instrumente bazate pe știință pentru viața de zi cu zi.",
        despre_autor = "Dr. Andrew Huberman este neurosavant și profesor la Universitatea Stanford.",
        parcurs_personal = "Am învățat multe despre importanța somnului și a luminii solare dimineața.",
        recomandat_daca = "Recomand acest podcast dacă ești pasionat de știință și vrei protocoale practice pentru sănătate.",
        addedAt = 3000
    )
)

private val LightColorScheme = lightColorScheme(
    primary = ThemePrimary,
    onPrimary = Color.White,
    background = ThemeBackground,
    onBackground = GeneralText,
    surface = ThemeBackground,
    onSurface = GeneralText,
    secondary = ThemeTextDark,
    onSecondary = Color.White
)

@Composable
fun StateChip(
    state: ResourceState,
    textStyle: TextStyle = MaterialTheme.typography.labelSmall,
    verticalPadding: androidx.compose.ui.unit.Dp = 2.dp
) {
    if (state == ResourceState.FRESH || state == ResourceState.NOT_OBTAINED) return

    val (color, icon, text) = when (state) {
        ResourceState.FINISHED -> Triple(
            Color(0xFF4CAF50), // Green
            Icons.Default.Check,
            stringResource(Res.string.state_finished)
        )
        ResourceState.ABANDONED -> Triple(
            Color(0xFFE57373), // Red
            Icons.Default.Close,
            stringResource(Res.string.state_abandoned)
        )
        else -> Triple(
            ThemePrimary.copy(alpha = 0.5f), // Neutral
            Icons.Default.Refresh, // Loading icon
            stringResource(Res.string.state_in_progress)
        )
    }

    Row(
        modifier = Modifier
            .background(color.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
            .padding(horizontal = 6.dp, vertical = verticalPadding),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = color,
            modifier = Modifier.size(12.dp)
        )
        Text(
            text = text,
            style = textStyle,
            color = color
        )
    }
}

@Composable
fun AppTheme(
    content: @Composable () -> Unit
) {
    val poppins = FontFamily(Font(Res.font.poppins_bold))
    val inter = FontFamily(Font(Res.font.inter_regular))

    val typography = Typography(
        headlineLarge = TextStyle(
            fontFamily = poppins,
            fontSize = 32.sp,
            fontWeight = FontWeight.Bold
        ),
        headlineMedium = TextStyle(
            fontFamily = poppins,
            fontSize = 28.sp,
            fontWeight = FontWeight.Bold
        ),
        bodyLarge = TextStyle(
            fontFamily = inter,
            fontSize = 16.sp
        ),
        bodyMedium = TextStyle(
            fontFamily = inter,
            fontSize = 14.sp
        )
    )

    MaterialTheme(
        colorScheme = LightColorScheme,
        typography = typography,
        content = content
    )
}
